import { FC, useContext, useState, useCallback, useEffect } from 'react';
import { RendererContext } from '../../../contexts/RendererContext';
import { usePersonaManager } from '../../../hooks/usePersonaManager';
import { ToolButton } from '../../../components/ToolButton';
import { Tooltip } from '../../../components/Tooltip';
import { T } from '../../../translation/src';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { ToolSettingsMenu } from '../ToolSettingsMenu';

/** Minimal tool shape needed to check for settings */
interface ToolWithSettings {
  enabled: boolean;
  getSettingsSchema?: () => Record<string, unknown> | undefined;
  shouldAutoOpenSettings?: () => boolean;
}

/** Minimal persona shape with tools map and event support */
interface PersonaWithTools {
  tools: Map<string, ToolWithSettings>;
  addEventListener(event: string, handler: () => void): void;
  removeEventListener(event: string, handler: () => void): void;
}

/**
 * ToolSettingsMenuButton - Settings button with menu for the currently active tool
 *
 * This component renders both the settings gear icon button and the settings menu.
 * It's positioned in the top-right corner of the renderer header bar.
 */
export const ToolSettingsMenuButton: FC = () => {
  const { viewer } = useContext(RendererContext);
  const { activePersona } = usePersonaManager(viewer);
  const [showSettings, setShowSettings] = useState(false);
  const [hasActiveToolSettings, setHasActiveToolSettings] = useState(false);

  const handleSettingsClick = useCallback(() => {
    setShowSettings((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setShowSettings(false);
  }, []);

  // Re-evaluate whenever the persona or its tools change
  useEffect(() => {
    const checkToolSettings = (): void => {
      if (!viewer?.personaManager || !activePersona) {
        setHasActiveToolSettings(false);
        return;
      }

      const persona = viewer.personaManager.getPersona(activePersona.name) as PersonaWithTools | undefined;
      if (!persona) {
        setHasActiveToolSettings(false);
        return;
      }

      const activeTool = Array.from(persona.tools.values()).find((tool) => tool.enabled);
      if (!activeTool) {
        setHasActiveToolSettings(false);
        return;
      }

      const hasSettings = typeof activeTool.getSettingsSchema === 'function' && activeTool.getSettingsSchema() !== undefined;
      setHasActiveToolSettings(hasSettings);
      // Tools whose settings are their primary UI (e.g. the sketch-object
      // library) pop the menu open on activation, so users discover it exists.
      if (hasSettings && activeTool.shouldAutoOpenSettings?.()) {
        setShowSettings(true);
      }
    };

    checkToolSettings();

    if (!viewer?.personaManager || !activePersona) return;

    const persona = viewer.personaManager.getPersona(activePersona.name) as PersonaWithTools | undefined;
    if (!persona) return;

    persona.addEventListener('tool_enabled', checkToolSettings);
    persona.addEventListener('tool_disabled', checkToolSettings);

    return () => {
      persona.removeEventListener('tool_enabled', checkToolSettings);
      persona.removeEventListener('tool_disabled', checkToolSettings);
    };
  }, [viewer, activePersona]);

  if (!hasActiveToolSettings) {
    return null;
  }

  return (
    <>
      <Tooltip message={<T _str="tool settings" swc />} delay={0}>
        <ToolButton icon={Cog6ToothIcon} name="tool settings" active={showSettings} disabled={false} onClick={handleSettingsClick} />
      </Tooltip>
      <ToolSettingsMenu visible={showSettings} onClose={handleClose} />
    </>
  );
};
